import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import camelcaseKeys from "camelcase-keys";
import { ApiCardTransaction } from "utils/api/cardTransactions";
import { API_PATH, getHeaders } from "utils/api/common";
import { ApiDashboard } from "utils/api/dashboard";
import { ApiOnboarding } from "utils/api/onboarding";
import {
  ApiUsersCurrentTransactionSource,
  ApiUsersProfile,
} from "utils/api/users";
import { decamelizeKeysDeep as decamelizeKeys } from "utils/object";

type ResponseData = any[] | Record<string, any>;

const apiFetchBaseQuery =
  (baseQueryOptions) => async (args, api, extraOptions) => {
    try {
      const response = await fetchBaseQuery(baseQueryOptions)(
        args,
        api,
        extraOptions
      );

      if (response.error?.status === 401) {
        window.location.pathname = "/users/sign_in";
      } else {
        const { data } = response;
        response.data = camelcaseKeys(data as ResponseData, { deep: true });
      }

      return response;
    } catch (error) {
      return { error };
    }
  };

const TAG_TYPES = [
  "Onboarding",
  "Dashboard",
  "UsersProfile",
  "CardTransactions",
];

const apiSlice = createApi({
  reducerPath: "api",
  baseQuery: apiFetchBaseQuery({
    baseUrl: API_PATH,
  }),
  tagTypes: TAG_TYPES,
  endpoints: (builder) => ({
    postOnboarding: builder.mutation<void, ApiOnboarding>({
      query: (body) => ({
        url: "/onboarding",
        method: "POST",
        headers: getHeaders(),
        body: decamelizeKeys(body),
      }),
      invalidatesTags: ["Onboarding", "Dashboard"],
    }),
    getOnboarding: builder.query<ApiOnboarding, void>({
      query: () => "/onboarding",
      providesTags: ["Onboarding"],
    }),
    getDashboard: builder.query<ApiDashboard, void>({
      query: () => "/dashboard",
      providesTags: ["Dashboard"],
    }),
    deleteUsersSignOut: builder.mutation<void, void>({
      query: () => ({
        url: "/users/sign_out",
        method: "DELETE",
        headers: getHeaders(),
      }),
    }),
    postUsersSignIn: builder.mutation<
      void,
      { user: { email: string; password: string } }
    >({
      query: (body) => ({
        url: "/users/sign_in",
        method: "POST",
        headers: getHeaders(),
        body: decamelizeKeys(body),
      }),
    }),
    postUsersSignUp: builder.mutation<
      void,
      { user: { email: string; password: string; product: string } }
    >({
      query: (body) => ({
        url: "/users/sign_up",
        method: "POST",
        headers: getHeaders(),
        body: decamelizeKeys(body),
      }),
    }),
    postUsersTransactionSettings: builder.mutation<
      void,
      { source: ApiUsersCurrentTransactionSource }
    >({
      query: (body) => ({
        url: "/users/update_transaction_source",
        method: "POST",
        headers: getHeaders(),
        body: decamelizeKeys(body),
      }),
      invalidatesTags: ["UsersProfile", "Dashboard"],
    }),
    getUsersProfile: builder.query<ApiUsersProfile, void>({
      query: () => "/users/profile",
      providesTags: ["UsersProfile"],
    }),
    getCardTransactions: builder.query<ApiCardTransaction[], void>({
      query: () => "/card_transactions",
      providesTags: ["CardTransactions"],
    }),
    postSeedTransaction: builder.mutation<void, void>({
      query: (body) => ({
        url: "/seed/transaction",
        method: "POST",
        headers: getHeaders(),
        body: decamelizeKeys(body),
      }),
      invalidatesTags: ["CardTransactions", "Dashboard"],
    }),
    postSeedReseed: builder.mutation<void, void>({
      query: () => ({
        url: "/seed/reseed",
        method: "POST",
        headers: getHeaders(),
      }),
      invalidatesTags: ["CardTransactions", "Dashboard", "UsersProfile"],
    }),
  }),
});

export const {
  usePostOnboardingMutation,
  useGetOnboardingQuery,
  useGetDashboardQuery,
  useDeleteUsersSignOutMutation,
  usePostUsersSignInMutation,
  usePostUsersSignUpMutation,
  usePostUsersTransactionSettingsMutation,
  useGetUsersProfileQuery,
  useGetCardTransactionsQuery,
  usePostSeedTransactionMutation,
  usePostSeedReseedMutation,
} = apiSlice;

export const getOnboarding = (options?) =>
  useGetOnboardingQuery(undefined, options);

export const getDashboard = (options?) =>
  useGetDashboardQuery(undefined, options);

export const getUsersProfile = (options?) =>
  useGetUsersProfileQuery(undefined, options);

export const getCardTransactions = (options?) =>
  useGetCardTransactionsQuery(undefined, options);

export default apiSlice;
